import React, { FC, useEffect } from "react";
import * as styles from "./DefaultSubscribeForm.module.scss";
import { useForm, Controller, FormProvider } from "react-hook-form";
import {
  Button,
  Select,
  InputText,
  InputCheckbox,
  PhoneInput,
  isValidPhoneNumber,
  TextArea,
  ModalFooter
} from "@silversea-ssc/ui-components-react";

import {
  sendLead,
  LeadAPIInvalidEmailError,
  FormRequestType
} from "src/utils/leadAPIClient";
import { LeadAPIRequest } from "src/utils/leadAPIClient";
import { t, Trans } from "@lingui/macro";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import AsyncForm from "components/Form/AsyncForm";

import {
  formDataToLeadAPIRequest,
  cruiseSuiteInfoToLeadFormData
} from "./utils";
import {
  getVisitorAuid,
  getMarketingCloudVisitorID,
  trackFormSubmitted
} from "src/utils/analytics/tracking";
import { LeadFormData } from "./types";
import InputWithError from "components/Form/InputWithError";

interface DefaultSubscribeFormProps {
  formName: string;
  email?: string;
  onSubmitSuccess: (
    formData: LeadFormData,
    requestData: LeadAPIRequest
  ) => void;
  requestSource: string;
  requestType: FormRequestType;
  brochureCode?: string;
  askImNotTraveAgent: boolean;
  askWorkWithAgent: boolean;
  askPhone: boolean;
  askDescription?: boolean;
  enableSubscribeEmail?: boolean;
  dataContentName?: string;
  cruiseSuiteInfo?: CruiseSuiteFormProps;
}

export interface CruiseSuiteFormProps {
  voyageName?: string;
  voyageCode?: string;
  departureDate?: string;
  voyageLength?: string;
  shipName?: string;
  price?: number;
  suiteVariation?: string;
}

const DefaultSubscribeForm: FC<DefaultSubscribeFormProps> = ({
  email,
  formName,
  onSubmitSuccess,
  requestSource,
  requestType,
  brochureCode,
  askImNotTraveAgent,
  askPhone,
  dataContentName,
  enableSubscribeEmail = true,
  askDescription = false,
  askWorkWithAgent,
  cruiseSuiteInfo = undefined
}) => {
  const language = useLanguage();
  const geolocation = useGeolocation();
  const visitorAuid = getVisitorAuid();
  const marketingCloudVisitorID = getMarketingCloudVisitorID();

  const defaultValues = React.useMemo(() => {
    const data: LeadFormData = {
      RequestSource: requestSource,
      RequestType: requestType,
      SiteCountry: geolocation?.countryCodeISO3 || "",
      SiteLanguage: language.code.toUpperCase(),
      SiteCurrency: geolocation.defaultCurrency,
      AUID: visitorAuid,
      MCId: marketingCloudVisitorID,
      WorkingWithAgent: askWorkWithAgent ? false : undefined,
      BrochuresRequested: brochureCode ? brochureCode : undefined,
      SubscribeEmail: enableSubscribeEmail ? "true" : undefined,
      IsNotAgent: askImNotTraveAgent ? true : undefined,
      Email: email && email.trim() != "" ? email : undefined,
      ...cruiseSuiteInfoToLeadFormData(cruiseSuiteInfo)
    };
    return data;
  }, [
    askImNotTraveAgent,
    askWorkWithAgent,
    brochureCode,
    cruiseSuiteInfo,
    email,
    enableSubscribeEmail,
    geolocation?.countryCodeISO3,
    geolocation.defaultCurrency,
    language.code,
    marketingCloudVisitorID,
    requestSource,
    requestType,
    visitorAuid
  ]);

  const formMethods = useForm<LeadFormData>({
    mode: "onChange",
    defaultValues: defaultValues
  });
  const { setValue, setError, formState, control, register } = formMethods;

  useEffect(() => {
    //ensure form has updated version of defaultValues (ie cruise/suite data)
    Object.keys(defaultValues).forEach((key) => {
      setValue(key as keyof LeadFormData, defaultValues[key], {
        shouldDirty: false,
        shouldValidate: false
      });
    });
  }, [defaultValues, setValue]);

  const onSubmit = async (data: LeadFormData) => {
    try {
      const request = formDataToLeadAPIRequest(data);
      const leadApiResponse = await sendLead(request);
      trackFormSubmitted(leadApiResponse);
      onSubmitSuccess(data, request);

      return true;
    } catch (e) {
      console.error("request", e);
      if (e instanceof LeadAPIInvalidEmailError) {
        setError("Email", {
          type: "invalid",
          message: "Incorrect email address"
        });
      }
    }
    return false;
  };

  return (
    <FormProvider {...formMethods}>
      <AsyncForm
        name={formName}
        onSubmit={onSubmit}
        className={styles.form}
        dataContentName={dataContentName}
      >
        <input ref={register} type="hidden" name="RequestSource" />
        <input ref={register} type="hidden" name="RequestType" />
        <input ref={register} type="hidden" name="SiteCountry" />
        <input ref={register} type="hidden" name="SiteLanguage" />
        <input ref={register} type="hidden" name="SiteCurrency" />
        <input ref={register} type="hidden" name="AUID" />
        <input ref={register} type="hidden" name="MCId" />
        {brochureCode && (
          <input ref={register} type="hidden" name="BrochuresRequested" />
        )}
        {enableSubscribeEmail && (
          <input ref={register} type="hidden" name="SubscribeEmail" />
        )}
        {cruiseSuiteInfo && (
          <React.Fragment>
            <input ref={register} type="hidden" name="VoyageCod" />
            <input ref={register} type="hidden" name="Voyage" />
            <input ref={register} type="hidden" name="Ship" />
            <input ref={register} type="hidden" name="SailDate" />
            <input ref={register} type="hidden" name="Price" />
            <input ref={register} type="hidden" name="SuiteVariation" />
          </React.Fragment>
        )}

        {askImNotTraveAgent && (
          <InputCheckbox
            ref={register}
            id="subscribe-modal-form-im-not-travel-agent"
            name="IsNotAgent"
          >
            <Trans id="Form ImNotTravelAgent">I am not a travel agent</Trans>
          </InputCheckbox>
        )}

        <InputWithError
          name="Title"
          id="subscribe-modal-title"
          label={`${t`Form Title`} *`}
        >
          <Select ref={register({ required: true })} defaultValue="">
            <option value=""></option>
            <option value="mr">{t`Form Title Mr.`}</option>
            <option value="mrs">{t`Form Title Mrs.`}</option>
            <option value="miss">{t`Form Title Miss.`}</option>
            <option value="ms">{t`Form Title Ms.`}</option>
            <option value="dr">{t`Form Title Dr.`}</option>
            <option value="dir">{t`Form Title Dir.`}</option>
            <option value="lady">{t`Form Title Lady.`}</option>
            <option value="prof">{t`Form Title Prof.`}</option>
            <option value="lord">{t`Form Title Lord.`}</option>
          </Select>
        </InputWithError>

        <InputWithError
          name="NameFirst"
          id="subscribe-modal-name-first"
          label={`${t`Form First name`} *`}
        >
          <InputText
            ref={register({
              required: true
            })}
            type="text"
          />
        </InputWithError>

        <InputWithError
          name="NameLast"
          id="subscribe-modal-name-last"
          label={`${t`Form Last name`} *`}
        >
          <InputText
            ref={register({
              required: true
            })}
            type="text"
          />
        </InputWithError>

        {askPhone && (
          <Controller
            name="Phone"
            control={control}
            defaultValue=""
            rules={{
              required: true,
              validate: (value) => {
                if (!isValidPhoneNumber(value)) return "invalid phone number";
                return true;
              }
            }}
            render={({ onChange, onBlur, value, name }) => (
              <InputWithError
                id="subscribe-modal-phone"
                name={name}
                label={`${t`Form Phone`} *`}
                forceLabelRenderAsFocused
              >
                <PhoneInput
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  country={geolocation?.countryCode}
                />
              </InputWithError>
            )}
          />
        )}

        <InputWithError
          id="subscribe-modal-email"
          name="Email"
          label={`${t`Form Email`} *`}
        >
          <InputText
            ref={register({
              required: true,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: "invalid email address"
              }
            })}
            type="email"
          />
        </InputWithError>
        {askDescription && (
          <InputWithError
            id="subscribe-modal-details"
            name="Comments"
            label={`${t`Details About Your Request`}`}
          >
            <TextArea ref={register()} />
          </InputWithError>
        )}

        <div className={styles.requiredFieldsLabel}>
          *<Trans>Required fields</Trans>
        </div>
        {askWorkWithAgent && (
          <div className={styles.extraFieldsContainer}>
            <InputCheckbox
              ref={register}
              id="subscribe-modal-form-working-with-agent"
              name="WorkingWithAgent"
              reverseValue={0}
            >
              <Trans id="Form WorkWithAgent">
                I am currently booked on a Silversea cruise
              </Trans>
            </InputCheckbox>
          </div>
        )}

        <div className={styles.disclaimerText}>
          By submitting this form I agree to receive electronic messages from
          Silversea including the latest news and offers. I may however
          unsubscribe from any communications at any time by clicking on this{" "}
          <a
            href="http://ws.silversea.com/sc/sc.aspx?actioncod=optout"
            target="_blank"
            rel="noopener noreferrer"
          >
            link
          </a>{" "}
          or visiting your MySilversea profile. Silversea Cruises Ltd.,
          Silversea Cruises Ltd. 333 SE 2nd Avenue, Suite 2600, Miami, Fl,
          33131, Unites States,{" "}
          <a href="http://www.silversea.com">www.silversea.com</a>,
          +1-800-722-9955
        </div>

        <ModalFooter>
          <Button
            className={styles.bodyFooterButton}
            renderAs="button"
            type="submit"
            centerText
            loading={formState.isSubmitting}
            disabled={!formState.isValid || formState.isSubmitting}
            data-testid="submit"
            data-content-piece="submit_button"
          >
            <Trans>SUBMIT</Trans>
          </Button>
        </ModalFooter>
      </AsyncForm>
    </FormProvider>
  );
};

export default DefaultSubscribeForm;
